/**
 * Assembles the canonical PPTX export payload from the Results UI state plus the
 * persisted track/schedule/vehicle configuration. Mirrors the shapes consumed
 * by the backend exporter (see smart-quick-sim-assets schema).
 */

import { Schedule, Vehicle, VehicleSimulationData } from '../../shared/DataTypes';
import { CapturedChart } from './chart-to-png';

/* eslint-disable @typescript-eslint/naming-convention -- The payload mirrors the backend's snake_case API contract. */

export interface PptxMeta {
  title: string;
  author?: string;
  created_at: string;
  app_version?: string;
  vehicle_type: string;
  scenario?: string;
  locale?: string;
}

export interface PptxConfigItem {
  label: string;
  value: string | number | boolean | null;
  unit?: string;
}

export interface PptxVehicle {
  vehicle_name: string;
  vehicle_scenario?: string;
  config?: PptxConfigItem[];
}

export interface PptxTrackItem {
  trackName?: string;
  stationName?: string;
  length_in_m?: number | null;
}

export interface PptxTrack {
  route_name: string;
  tracks: PptxTrackItem[];
}

export interface PptxDrivingDataItem {
  track_name?: string;
  from_station?: string;
  to_station?: string | null;
  driving_time_in_seconds?: number | null;
}

export interface PptxStoppingDataItem {
  track_name?: string;
  station_name?: string;
  stopping_time_in_seconds?: number | null;
}

export interface PptxSchedule {
  timetable_name: string;
  driving_data?: PptxDrivingDataItem[];
  stopping_data?: PptxStoppingDataItem[];
}

export interface PptxResults {
  info: Record<string, unknown>;
  result_table: Record<string, unknown>[];
  energy_consumption_results: Record<string, unknown>;
}

export interface PptxChart {
  id: string;
  title: string;
  source_section: 'plots' | 'usercharts';
  png_base64: string;
  width_px?: number;
  height_px?: number;
}

const PPTX_TRANSLATION_KEYS = {
  cover_subtitle: 'PPTX.COVER_SUBTITLE',
  vehicle_section: 'VEHICLE.VEHICLE',
  vehicle_configuration: 'PPTX.VEHICLE_CONFIGURATION',
  schedule_section: 'SCHEDULE.SCHEDULE',
  schedule_itinerary: 'PPTX.SCHEDULE_ITINERARY',
  results_section: 'USERCHARTS.SIMULATION_RESULTS',
  simulation_info: 'RESULTS.INFOS.HEADING',
  energy_consumption: 'RESULTS.TABLES.HEADING_ENERGY_CONSUMPTION',
  driving_times: 'RESULTS.TABLES.HEADING_DRIVING_TIMES',
  parameter: 'TRACK.PARAMETER',
  value: 'PPTX.VALUE',
  station: 'TRACK.COLUMN_NAMES.station_name',
  stopping_time: 'RESULTS.TABLES.STOPPING_TIME',
  next_station: 'PPTX.NEXT_STATION',
  driving_time: 'PPTX.DRIVING_TIME',
  departure_stop: 'RESULTS.TABLES.DEPARTURE_STOP',
  destination_stop: 'RESULTS.TABLES.DESTINATION_STOP',
  position: 'RESULTS.TABLES.ABSOLUTE_POSITION',
  distance: 'RESULTS.TABLES.RELATIVE_DISTANCE',
  target_time: 'RESULTS.TABLES.TARGET_TRAVEL_TIME',
  actual_time: 'RESULTS.TABLES.ACTUAL_TRAVEL_TIME',
  difference: 'RESULTS.TABLES.DIFFERENCE',
  cumulative_difference: 'RESULTS.TABLES.CUMULATIVE_DIFFERENCE',
  average_speed: 'RESULTS.TABLES.AVERAGE_SPEED',
  soe: 'RESULTS.TABLES.SOE',
  hydrogen: 'PPTX.HYDROGEN_MASS',
  simulation_errors: 'RESULTS.INFOS.SIMULATION_ERRORS',
  simulation_warnings: 'RESULTS.INFOS.SIMULATION_WARNINGS',
  sufficient_energy: 'RESULTS.INFOS.SUFFICIENT_ENERGY',
  schedule_fulfilled: 'RESULTS.INFOS.SCHEDULE_FULFILLED',
  energy_consumed: 'RESULTS.TABLES.ENERGY_CONSUMPTION',
  energy_recuperated: 'RESULTS.TABLES.ENERGY_RECUPERATION',
  net_energy: 'RESULTS.TABLES.NET_ENERGY_CONSUMPTION',
  track: 'TRACK.TRACK',
  vehicle_type: 'VEHICLE.TYPE',
  scenario: 'VEHICLE.SCENARIO',
  driving_style: 'VEHICLE.DRIVING_STYLE',
  available_soe: 'VEHICLE.AVAILABLE_SOE',
  available_h2: 'VEHICLE.AVAILABLE_H2',
  result_chart: 'PPTX.RESULT_CHART'
} as const;

export type PptxLabels = Record<keyof typeof PPTX_TRANSLATION_KEYS, string>;

export const buildPptxLabels = (translate: (key: string) => string): PptxLabels =>
  Object.fromEntries(
    Object.entries(PPTX_TRANSLATION_KEYS).map(([label, key]) => [label, translate(key)])
  ) as PptxLabels;

export interface PptxExportPayload {
  meta: PptxMeta;
  labels: PptxLabels;
  vehicle: PptxVehicle;
  track: PptxTrack;
  schedule: PptxSchedule;
  results: PptxResults;
  charts?: PptxChart[];
}

export interface PptxPayloadInputs {
  results: Record<string, unknown>;
  schedule: Schedule | null;
  vehicle: Vehicle | null;
  vehicleSimulationData: VehicleSimulationData | null;
  charts: CapturedChart[];
  labels: PptxLabels;
  locale?: string;
  author?: string;
  appVersion?: string;
}

const asRecord = (value: unknown): Record<string, unknown> => {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
};

const asArray = (value: unknown): Record<string, unknown>[] => {
  return Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
};

const buildTrackAndSchedule = (
  schedule: Schedule | null
): {
  track: PptxTrack;
  pptxSchedule: PptxSchedule;
} => {
  const drivingData: PptxDrivingDataItem[] = [];
  const stoppingData: PptxStoppingDataItem[] = [];
  const tracks: PptxTrackItem[] = [];
  const trackNames = new Set<string>();

  for (const trackInSchedule of schedule?.tracks ?? []) {
    const trackDrivingData = trackInSchedule.scheduleData?.drivingData ?? [];
    const trackStoppingData = trackInSchedule.scheduleData?.stoppingData ?? [];

    for (const driving of trackDrivingData) {
      drivingData.push({
        track_name: driving.trackName,
        from_station: driving.fromStation,
        to_station: driving.toStation,
        driving_time_in_seconds: driving.drivingTime
      });
      if (driving.trackName) {
        trackNames.add(driving.trackName);
      }
    }
    for (const stopping of trackStoppingData) {
      stoppingData.push({
        track_name: stopping.trackName,
        station_name: stopping.stationName,
        stopping_time_in_seconds: stopping.stoppingTime
      });
      tracks.push({ trackName: stopping.trackName, stationName: stopping.stationName });
      if (stopping.trackName) {
        trackNames.add(stopping.trackName);
      }
    }
  }

  const routeName =
    trackNames.size > 0 ? Array.from(trackNames).join(' / ') : (schedule?.name ?? '');

  return {
    track: { route_name: routeName, tracks },
    pptxSchedule: {
      timetable_name: schedule?.name ?? '',
      driving_data: drivingData,
      stopping_data: stoppingData
    }
  };
};

const buildVehicle = (
  vehicle: Vehicle | null,
  vehicleSimulationData: VehicleSimulationData | null,
  labels: PptxLabels
): PptxVehicle => {
  const scenarioName = vehicleSimulationData?.scenario?.name;
  const config: PptxConfigItem[] = [];

  if (vehicle?.type) {
    config.push({ label: labels.vehicle_type, value: vehicle.type });
  }
  if (scenarioName) {
    config.push({ label: labels.scenario, value: scenarioName });
  }
  if (vehicleSimulationData?.drivingStyle) {
    config.push({ label: labels.driving_style, value: vehicleSimulationData.drivingStyle });
  }
  if (vehicleSimulationData?.availableFuel !== undefined) {
    config.push({
      label: vehicle?.type === 'HEMU' ? labels.available_h2 : labels.available_soe,
      value: vehicleSimulationData.availableFuel,
      unit: '%'
    });
  }

  return {
    vehicle_name: vehicle?.name ?? labels.vehicle_section,
    vehicle_scenario: scenarioName,
    config: config.length > 0 ? config : undefined
  };
};

export const buildPptxPayload = (inputs: PptxPayloadInputs): PptxExportPayload => {
  const {
    results,
    schedule,
    vehicle,
    vehicleSimulationData,
    charts,
    labels,
    locale,
    author,
    appVersion
  } = inputs;

  const simulationResults = asRecord(results['simulation_results']);
  const energyConsumption = asRecord(results['energy_consumption_results']);
  const vehicleType = String(results['vehicle_type'] ?? '');

  const { track, pptxSchedule } = buildTrackAndSchedule(schedule);

  const timetableName =
    pptxSchedule.timetable_name || String(energyConsumption['timetable_name'] ?? '');
  pptxSchedule.timetable_name = timetableName;

  const routeName = track.route_name || timetableName;
  track.route_name = routeName;

  return {
    meta: {
      title: `${labels.track}: ${routeName}`,
      created_at: new Date().toISOString(),
      author,
      app_version: appVersion,
      vehicle_type: vehicleType,
      scenario: vehicleSimulationData?.scenario?.name,
      locale
    },
    labels,
    vehicle: buildVehicle(vehicle, vehicleSimulationData, labels),
    track,
    schedule: pptxSchedule,
    results: {
      info: asRecord(simulationResults['info']),
      result_table: asArray(results['result_table']),
      energy_consumption_results: energyConsumption
    },
    charts: charts.map(chart => ({
      id: chart.id,
      title: chart.title || labels.result_chart,
      source_section: chart.sourceSection,
      png_base64: chart.pngBase64,
      width_px: chart.widthPx,
      height_px: chart.heightPx
    }))
  };
};
